import { HttpClient, HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AumicoAbcpConfig, AumicoAbcpConfigService } from '../abcp-data.config';
import { DocumentFilterKey, DocumentSort } from '../components/documents-hub/documents-hub.types';
import {
    IClosingAccountOption,
    IClosingAccountsResponse,
    IClosingDocumentDetail,
    IClosingDocumentListResponse,
    ISetAssignmentRequest,
    ISharepointFetchResponse,
    ISyncRun,
    ISyncStartResult,
    ISharepointListResponse,
} from '../models/closing-document.model';

/**
 * HTTP for the ABCP closing documents (REV-21376, `ClosingDocumentsController` in `aumico-be`).
 *
 * ⚠️ THE ASSIGNMENT CALLS MOVED IN (2026-08-20, TL decision) — they were REV-21602's and this class
 * deliberately could not express them. `acceptProposal`/`rejectProposal` (#84) call the routes #57's
 * auto-assignment needs a human compensation for — see `ClosingDocumentEffects.acceptProposal$`.
 *
 * ⚠️ NO delete. There is no `DELETE` on a document anywhere in this flow (§4); the only removal is
 * `setNotRelevant`, which is reversible.
 */
@Injectable()
export class ClosingDocumentService {
    private readonly baseUrl: string;

    public constructor(
        private readonly http: HttpClient,
        @Inject(AumicoAbcpConfigService) config: AumicoAbcpConfig,
    ) {
        // NO leading slash: `apiGateway` already ENDS with one (`http://localhost:3800/api/`), so
        // `${config.apiGateway}/projects` yields `/api//projects` and the gateway 404s. Same
        // concatenation as `integration.service.ts:87-89`.
        this.baseUrl = `${config.apiGateway}projects`;
    }

    /**
     * The hub's read.
     *
     * `search` narrows the THREE COUNTS server-side — not the rows: the rows come back per pill and
     * the client filters them, because the hub can only render one active pill.
     *
     * `sort` orders the ROWS server-side. The client no longer sorts at all: the server can order by
     * columns the client cannot reconstruct, and two comparators over one column is how a table and
     * its own header end up disagreeing.
     *
     * `assignedBy` (REV-21901 N3/Weg 1, W1) narrows the ROWS server-side to the machine-assigned set
     * — the counts stay whole-closing, like with `accountId`. This client only ever sends `'agent'`:
     * the toggle knows one gesture; the server accepts more, and that is its business.
     */
    public listDocuments(
        projectId: string,
        filter: DocumentFilterKey,
        search: string,
        sort: DocumentSort | null,
        assignedBy: 'agent' | null = null,
    ): Observable<IClosingDocumentListResponse> {
        let params = new HttpParams().set('filter', filter);
        // Omitted rather than sent blank, so an unsearched load is byte-for-byte the request it always
        // was — and so the server never has to tell `search=` apart from no search at all.
        const term = search.trim();
        if (term.length > 0) {
            params = params.set('search', term);
        }
        // Same rule: no sort means no parameters, not `sort=&order=`. The route defaults to the hub's
        // own newest-first, so an unsorted request and the first paint agree without coordination.
        if (sort != null) {
            params = params.set('sort', sort.key).set('order', sort.direction);
        }
        // Same rule again — and stricter than for `search`: `assignedBy=` on the wire is not "off",
        // it is ANOTHER value and the server answers 400 for it (the facet is an enum, not free
        // text). Omitting the key is the only correct form of "off".
        if (assignedBy != null) {
            params = params.set('assignedBy', assignedBy);
        }
        return this.http.get<IClosingDocumentListResponse>(`${this.baseUrl}/${projectId}/documents`, { params });
    }

    /**
     * The documents filed to ONE account — the single-account page's list.
     *
     * Always the `assigned` pill, because a document filed to an account is by definition in it; and
     * `accountId` narrows within that. Its own method rather than an argument on `listDocuments`: the
     * hub must never accidentally send an account and quietly show a fraction of the closing.
     */
    public listAccountDocuments(
        projectId: string,
        accountId: string,
    ): Observable<IClosingDocumentListResponse> {
        const params = new HttpParams().set('filter', 'assigned').set('accountId', accountId);
        return this.http.get<IClosingDocumentListResponse>(`${this.baseUrl}/${projectId}/documents`, { params });
    }

    public getDocument(projectId: string, documentId: string): Observable<IClosingDocumentDetail> {
        return this.http.get<IClosingDocumentDetail>(`${this.baseUrl}/${projectId}/documents/${documentId}`);
    }

    /**
     * REV-21369 — die entschlüsselten Originalbytes für den Beleg-Viewer (ADR-0021 E3).
     *
     * ⚠️ Als `ArrayBuffer` über DIESEN Client (Bearer über den Interceptor), nie als URL an pdf.js:
     * sonst bräuchte die URL das Token oder wäre eine signierte Klartext-URL. Die Bytes gehen NICHT in
     * den Store — ein `ArrayBuffer` ist kein serialisierbarer Zustand.
     */
    public getContent(projectId: string, documentId: string): Observable<ArrayBuffer> {
        return this.http.get(`${this.baseUrl}/${projectId}/documents/${documentId}/content`, {
            responseType: 'arraybuffer',
        });
    }

    /** Dieselbe Tür als Download (Freigabe Q3: Herunterladen ja, Drucken nein) — das Original, ohne Markierungen. */
    public downloadContent(projectId: string, documentId: string): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/${projectId}/documents/${documentId}/content`, {
            params: new HttpParams().set('disposition', 'attachment'),
            responseType: 'blob',
        });
    }

    /**
     * One request PER FILE, not one multipart request with many parts.
     *
     * The route takes a single `file` field (`FileInterceptor`), and per-file requests are what make
     * PARTIAL success expressible: the modal's `[failures]` is per file, so one 415 must not discard
     * the files that were fine. A single batched request would have to fail or succeed as a whole.
     */
    public uploadDocument(projectId: string, file: File, accountId?: string): Observable<IClosingDocumentDetail> {
        const body = new FormData();
        body.append('file', file, file.name);
        // The server files the document to this account in the same request — no second call that
        // could fail and leave it in Eingang.
        if (accountId != null) {
            body.append('accountId', accountId);
        }
        return this.http.post<IClosingDocumentDetail>(`${this.baseUrl}/${projectId}/documents`, body);
    }

    /** The reversible flag — one route, both directions (ADR-0009 §4). */
    public setNotRelevant(
        projectId: string,
        documentId: string,
        notRelevant: boolean,
    ): Observable<IClosingDocumentDetail> {
        return this.http.patch<IClosingDocumentDetail>(
            `${this.baseUrl}/${projectId}/documents/${documentId}/not-relevant`,
            { notRelevant },
        );
    }

    /**
     * The account plan a document can be filed to — the closing's REAL chart.
     *
     * `accounts/plan` and not `accounts`: the latter is the account surface's list route, which
     * aggregates balances and therefore downloads and decrypts a journal blob per period. A picker
     * needs numbers and names, and must not pay for figures it never renders.
     *
     * ⚠️ It used to call `accounts`, served from a fixed fixture by a SECOND handler that REV-21376
     * declared on that same path. Both handlers existed after the merge and only one of them was ever
     * reached; the fixture one is gone, and this route answers from the closing's own chart.
     *
     * Fetched once per closing rather than per picker: both pickers show the same list, and
     * re-requesting it on every open would be the same bytes again.
     */
    public listAccounts(projectId: string): Observable<IClosingAccountOption[]> {
        return this.http.get<IClosingAccountsResponse>(`${this.baseUrl}/${projectId}/accounts/plan`).pipe(
            map((response) =>
                response.accounts.map((entry) => ({
                    id: entry.accountId,
                    number: entry.accountNumber,
                    name: entry.name,
                    accountGroup: entry.accountGroup,
                })),
            ),
        );
    }

    /**
     * F1/F4 (#84) — accept or reject the machine proposal that #57's auto-assignment produces.
     *
     * `POST …/proposal/{accept|reject}`, answering with the UPDATED `IClosingDocumentDetail` — same
     * response shape as `setAssignment`, so the effect can refresh both surfaces from one round trip.
     * Accept takes the chosen candidate (`accountId`); without it the backend files the proposed account.
     */
    public acceptProposal(projectId: string, documentId: string, accountId?: string): Observable<IClosingDocumentDetail> {
        return this.http.post<IClosingDocumentDetail>(
            `${this.baseUrl}/${projectId}/documents/${documentId}/proposal/accept`,
            accountId ? { accountId } : {},
        );
    }

    public rejectProposal(projectId: string, documentId: string): Observable<IClosingDocumentDetail> {
        return this.http.post<IClosingDocumentDetail>(
            `${this.baseUrl}/${projectId}/documents/${documentId}/proposal/reject`,
            {},
        );
    }

    /** File a document to an account or the Projekt-Akte. `PUT` — the body is the whole assignment. */
    public setAssignment(
        projectId: string,
        documentId: string,
        request: ISetAssignmentRequest,
    ): Observable<IClosingDocumentDetail> {
        return this.http.put<IClosingDocumentDetail>(
            `${this.baseUrl}/${projectId}/documents/${documentId}/assignment`,
            request,
        );
    }

    /**
     * Un-file — back to Eingang.
     *
     * A DELETE on the ASSIGNMENT, never on the document: there is no delete for a document anywhere in
     * this flow (ADR-0009 §4), and the two must not be confusable at any layer.
     */
    public clearAssignment(projectId: string, documentId: string): Observable<IClosingDocumentDetail> {
        return this.http.delete<IClosingDocumentDetail>(
            `${this.baseUrl}/${projectId}/documents/${documentId}/assignment`,
        );
    }

    /** The Dauerakte marking on its own — it does not touch the filing. */
    public setValidity(
        projectId: string,
        documentId: string,
        validity: 'closing' | 'longTerm',
    ): Observable<IClosingDocumentDetail> {
        return this.http.patch<IClosingDocumentDetail>(
            `${this.baseUrl}/${projectId}/documents/${documentId}/validity`,
            { validity },
        );
    }

    /** What is in the bound SharePoint folders right now. Read-only — imports nothing. */
    public listSharepointFiles(projectId: string): Observable<ISharepointListResponse> {
        return this.http.get<ISharepointListResponse>(`${this.baseUrl}/${projectId}/sharepoint-files`);
    }

    /**
     * Pull every not-yet-imported PDF from every bound folder — the hub's "Jetzt synchronisieren".
     *
     * No body: the only gesture the merged pure view offers is fetch-all. Idempotent server-side, so a
     * double click costs a round trip and creates nothing.
     */
    public fetchSharepointFiles(projectId: string): Observable<ISharepointFetchResponse> {
        return this.http.post<ISharepointFetchResponse>(`${this.baseUrl}/${projectId}/sharepoint-files/fetch`, {});
    }

    /**
     * ⚠️ The three routes below live on `sharepoint-files`, NOT on `documents` — they are served by
     * `ClosingSharepointFilesController`, next to `fetch`, because they are the SharePoint surface.
     * They were written against `documents/sync` first and every call 404'd; nothing in the type
     * system can catch a wrong URL string, and only starting the app did.
     */

    /**
     * Start a sync and return at once (REV-21964 · R3) — what "Jetzt synchronisieren" calls now.
     *
     * Answers with a run id the moment the run is persisted, not when the import has finished. The
     * method above imports INSIDE the request: at ten documents that is invisible, at the five
     * hundred the cap allows the request times out, the user is shown a failure, and the documents
     * that WERE imported stay invisible until they reload.
     *
     * ⚠️ A refusal is not an error. `accepted: false` with `closingSealed`, `alreadyRunning` or
     * `noBoundFolders` is a 200 carrying an answer the button can say out loud — and for
     * `alreadyRunning` the `runId` is the run already in flight, so the UI can simply follow it.
     */
    public startSharepointSync(projectId: string): Observable<ISyncStartResult> {
        return this.http.post<ISyncStartResult>(`${this.baseUrl}/${projectId}/sharepoint-files/sync`, {});
    }

    /** One run's report — counts and per-file reasons, surviving a reload (U1). */
    public getSyncRun(projectId: string, runId: string): Observable<ISyncRun> {
        return this.http.get<ISyncRun>(`${this.baseUrl}/${projectId}/sharepoint-files/sync/runs/${runId}`);
    }

    /**
     * The newest run, so the hub can show the last result without holding an id.
     *
     * `null` when the closing has never been synced — an empty state, not a failure.
     */
    public getLatestSyncRun(projectId: string): Observable<ISyncRun | null> {
        return this.http.get<ISyncRun | null>(`${this.baseUrl}/${projectId}/sharepoint-files/sync/runs`);
    }
}
